import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableImageProps {
  src: string;
  alt: string;
  onChange: (url: string) => void;
  className?: string;
  aspectRatio?: string;
  fallbackSrc?: string;
}

export function EditableImage({
  src,
  alt,
  onChange,
  className,
  aspectRatio = '4/3',
  fallbackSrc
}: EditableImageProps) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [uploading, setUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!assertCanWrite()) return;
    inputRef.current?.click();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!assertCanWrite()) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${orgId}/homepage/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('content-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('content-images')
        .getPublicUrl(filePath);

      onChange(publicUrl);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const displaySrc = src || fallbackSrc;

  return (
    <div
      className={cn(
        'relative cursor-pointer group overflow-hidden',
        className
      )}
      style={{ aspectRatio }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        disabled={!canWrite}
        onChange={handleUpload}
        className="hidden"
      />
      
      {displaySrc ? (
        <img
          src={displaySrc}
          alt={alt}
          className="w-full h-full object-cover transition-all"
        />
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-muted-foreground" />
        </div>
      )}

      {/* Hover overlay */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity',
          canWrite && (isHovered || uploading) ? 'opacity-100' : 'opacity-0'
        )}
      >
        {uploading ? (
          <div className="text-white text-sm">Uploading...</div>
        ) : (
          <div className="text-white text-center">
            <Upload className="w-8 h-8 mx-auto mb-2" />
            <span className="text-sm">Click to change</span>
          </div>
        )}
      </div>
    </div>
  );
}